/**
 * ActiveCallIndicator — floating bottom-center pill that represents an
 * in-flight call while no full call surface is on screen. Visual port of the
 * demo repo's ActiveCallBar (demo-ironpipeline PhoneCallLayer), adapted to
 * the production palette and the real twilio.context state.
 *
 * Two flows hand their call over to this widget:
 *
 *   - Popup flow, MINIMIZED: the agent hit Minimize on the CallLeadPopup to
 *     navigate mid-call. Renders an "Expand" button that brings the popup
 *     back (maximizeCall). While the popup is EXPANDED it is its own
 *     indicator, so we hide.
 *   - Route-driven flow (`/call-center/call-lead/{sessionName}`): once the
 *     agent navigates out, the call view unmounts and nothing on screen
 *     acknowledges the live call. Renders "Return", which navigates back to
 *     the originating route.
 *
 * Visibility matrix:
 *
 *   isCallActive     | popup            | on originPath | render?
 *   -----------------|------------------|---------------|---------
 *   false            | —                | —             | no
 *   true             | open (expanded)  | —             | no  (popup is the indicator)
 *   true             | open (minimized) | —             | YES (Expand button)
 *   true             | closed           | true          | no  (call view is on screen)
 *   true             | closed           | false         | YES (Return button)
 */
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowUpRight,
  Maximize2,
  Mic,
  MicOff,
  PauseCircle,
  PhoneOff,
  PlayCircle,
} from "lucide-react";
import { SESSION_STATUS_IN_PROGRESS } from "@icebergeradmin/common";
import { useTwilioContext } from "@/context/twilio.context";
import { useCallLeadPopup } from "@/context/call.lead.popup.context";
import { cn } from "@/lib/utils";

const formatDuration = (ms: number) => {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

const ActiveCallIndicator = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    state,
    isCallActive,
    isMuted,
    isOnHold,
    callStartedAt,
    leadName,
    phoneNumber,
    originPath,
    leadId,
    sessionName,
    mute,
    unmute,
    hold,
    unhold,
    hangup,
  } = useTwilioContext();
  const { isOpen: popupOpen, isMinimized, maximizeCall } = useCallLeadPopup();

  // Tick once a second while in-call so the duration display updates.
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (state !== "in-call") return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [state]);

  const visible =
    isCallActive &&
    // The EXPANDED popup is its own persistent indicator — never compete with
    // it. A MINIMIZED popup call is exactly what this widget exists for.
    !(popupOpen && !isMinimized) &&
    // If the agent is already on the originating route, the call view is on
    // screen and the indicator is redundant.
    !(originPath && location.pathname === originPath);

  const calling = state === "initializing" || state === "connecting" || state === "ringing";

  const statusLabel = calling
    ? "Calling…"
    : isOnHold
      ? "On hold"
      : "Active call";

  const dotClasses = calling
    ? "bg-blue-400"
    : isOnHold
      ? "bg-amber-400"
      : "bg-emerald-400";

  const handleReturn = () =>
    navigate(originPath as string, {
      state:
        sessionName && leadId !== null
          ? {
              lead: {
                sessionName,
                leadId,
                name: leadName ?? phoneNumber ?? "",
                phoneNumber: phoneNumber ?? undefined,
                email: "",
                role: 0 as const,
                sessionStatus: SESSION_STATUS_IN_PROGRESS,
                startDate: new Date().toISOString(),
              },
            }
          : undefined,
    });

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          role="region"
          aria-label="Active call"
          // Centering lives in the motion props: framer-motion owns the
          // element's `transform`, so a Tailwind -translate-x-1/2 would be
          // overwritten on the first animation frame.
          initial={{ opacity: 0, y: 24, x: "-50%" }}
          animate={{ opacity: 1, y: 0, x: "-50%" }}
          exit={{ opacity: 0, y: 24, x: "-50%" }}
          transition={{ type: "spring", stiffness: 380, damping: 32 }}
          className="fixed bottom-6 left-1/2 z-40 flex items-center gap-3 rounded-2xl border border-white/10 bg-gradient-to-b from-[#1B1F28]/95 to-[#0D1018]/95 py-2.5 pl-4 pr-2.5 shadow-[0_10px_40px_rgba(0,0,0,0.5)] backdrop-blur-md"
        >
          {/* Pulsing status dot */}
          <span className="relative flex h-2.5 w-2.5">
            <span
              className={cn(
                "absolute inline-flex h-full w-full animate-ping rounded-full opacity-60",
                dotClasses,
              )}
            />
            <span className={cn("relative inline-flex h-2.5 w-2.5 rounded-full", dotClasses)} />
          </span>

          {/* Identity + status */}
          <div className="min-w-0 max-w-[220px]">
            <p className="truncate text-[13px] font-semibold leading-tight text-white">
              {leadName ?? phoneNumber ?? "Active call"}
            </p>
            <p className="truncate text-[11px] leading-tight text-white/50">
              {statusLabel}
              {phoneNumber && ` · ${phoneNumber}`}
            </p>
          </div>

          {/* Duration — only once connected */}
          {state === "in-call" && callStartedAt && (
            <span className="font-mono text-xs font-semibold tabular-nums text-emerald-300">
              {formatDuration(now - callStartedAt.getTime())}
            </span>
          )}

          {/* Controls */}
          <div className="ml-1 flex items-center gap-1.5">
            {popupOpen && isMinimized ? (
              <button
                type="button"
                onClick={maximizeCall}
                aria-label="Expand call window"
                title="Expand call window"
                className="grid h-9 w-9 place-items-center rounded-xl border border-white/10 bg-white/5 text-white/80 transition-colors hover:bg-white/10 hover:text-white"
              >
                <Maximize2 className="h-4 w-4" />
              </button>
            ) : originPath ? (
              <button
                type="button"
                onClick={handleReturn}
                aria-label="Return to call screen"
                title="Return to call screen"
                className="grid h-9 w-9 place-items-center rounded-xl border border-white/10 bg-white/5 text-white/80 transition-colors hover:bg-white/10 hover:text-white"
              >
                <ArrowUpRight className="h-4 w-4" />
              </button>
            ) : null}

            <button
              type="button"
              onClick={isMuted ? unmute : mute}
              disabled={state !== "in-call" || isOnHold}
              aria-label={isMuted ? "Unmute" : "Mute"}
              title={isMuted ? "Unmute" : "Mute"}
              className={cn(
                "grid h-9 w-9 place-items-center rounded-xl border transition-colors",
                isMuted && !isOnHold
                  ? "border-white/20 bg-white/15 text-white"
                  : "border-white/10 bg-white/5 text-white/80 hover:bg-white/10 hover:text-white",
                (state !== "in-call" || isOnHold) &&
                  "cursor-not-allowed opacity-40 hover:bg-white/5 hover:text-white/80",
              )}
            >
              {isMuted ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
            </button>

            <button
              type="button"
              onClick={isOnHold ? unhold : hold}
              disabled={state !== "in-call"}
              aria-label={isOnHold ? "Resume" : "Hold"}
              title={isOnHold ? "Resume" : "Hold"}
              className={cn(
                "grid h-9 w-9 place-items-center rounded-xl border transition-colors",
                isOnHold
                  ? "border-amber-500/40 bg-amber-500/20 text-amber-300"
                  : "border-white/10 bg-white/5 text-white/80 hover:bg-white/10 hover:text-white",
                state !== "in-call" &&
                  "cursor-not-allowed opacity-40 hover:bg-white/5 hover:text-white/80",
              )}
            >
              {isOnHold ? <PlayCircle className="h-4 w-4" /> : <PauseCircle className="h-4 w-4" />}
            </button>

            <button
              type="button"
              onClick={hangup}
              aria-label="End call"
              title="End call"
              className="grid h-9 w-11 place-items-center rounded-xl bg-red-500 text-white shadow-lg shadow-red-500/20 transition-colors hover:bg-red-600 active:bg-red-700"
            >
              <PhoneOff className="h-4 w-4" />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ActiveCallIndicator;
